import { Injectable } from '@angular/core'
import * as CryptoJS from 'crypto-js'
import { UserInputService } from 'src/app/common/api/services/user-input.service'

@Injectable({
  providedIn: 'root'
})
export class GtmImplementation {
  skipNextPageView: boolean = false

  constructor (
    public userInputService: UserInputService
  ) {}

  private getDataLayer (): any[] {
    (window as any).dataLayer = (window as any).dataLayer || []
    return (window as any).dataLayer
  }

  private getUserIdHash (): string {
    const email = this.userInputService.getValue('primary_email')
    const emailStr = typeof email === 'string' ? email : (email?.option_label || email?.option_value || '')
    if (!emailStr) return ''
    return CryptoJS.SHA256(emailStr).toString(CryptoJS.enc.Hex)
  }

  private getStringValue (value: any): string {
    if (!value) return 'na'
    if (typeof value === 'string') return value.replace(/ /g, '_').toLowerCase()
    if (Array.isArray(value)) {
      const labels = value.map((item: any) => item.option_desc || item.option_label || item.option_value || '').filter(Boolean)
      return labels.length ? labels.join('|').replace(/ /g, '_').toLowerCase() : 'na'
    }
    if (typeof value === 'object') {
      const str = value.option_desc || value.option_label || value.option_value || ''
      return typeof str === 'string' ? str.replace(/ /g, '_').toLowerCase() : 'na'
    }
    return 'na'
  }

  private getCounsellingMode (): string {
    const mode = localStorage.getItem('counsellingMode')
    return mode ? mode.replace(/[ -]/g, '_').toLowerCase() : 'na'
  }

  private getOfficeId (): string {
    const sfResponse = localStorage.getItem('sfResponse')
    if (!sfResponse) return 'na'
    try {
      const officeDetails = JSON.parse(sfResponse)
      return (officeDetails?.wir_counsellor_team || 'na').toLowerCase()
    } catch {
      return 'na'
    }
  }

  private getOfficeLocation (): string {
    const sfResponse = localStorage.getItem('sfResponse')
    if (!sfResponse) return 'na'
    try {
      const officeDetails = JSON.parse(sfResponse)
      const country = officeDetails?.wir_cognito_country
      return country ? country.replace(/ /g, '_').toLowerCase() : 'na'
    } catch {
      return 'na'
    }
  }

  pageView (pageName: any, userLoginStatus: any, featureGTM: any): void {
    if (this.skipNextPageView) {
      this.skipNextPageView = false
      return
    }
    const payload: any = {
      event: 'pageview',
      destination_country: this.getStringValue(this.userInputService.getValue('preferredCountryCode')),
      feature: featureGTM,
      institution_id: 'na',
      institution_name: 'na',
      keyword: 'na',
      pagename: pageName,
      system_type: 'wir_counsellor',
      parent_category: 'na',
      qualification_type: this.getStringValue(this.userInputService.getValue('qualificationType')),
      referral_page: 'na',
      office_id: this.getOfficeId(),
      study_level: this.getStringValue(this.userInputService.getValue('preferredStudyLevel')),
      intake_year: this.getStringValue(this.userInputService.getValue('studyPlanTimeline')),
      office_location: this.getOfficeLocation(),
      user_login_status: userLoginStatus
    }

    if (pageName !== 'email_page' && pageName !== 'sign_in' && pageName !== 'sign_up' && pageName !== 'forgot_pwd_success' && pageName !== 'forgot_pwd_email_id' && pageName !== 'forgot_pwd_otp_and_password') {
      const userId = this.getUserIdHash()
      if (userId) {
        payload.user_id = userId
      }
    }
    this.getDataLayer().push(payload)
  }

  trackKPI (eventnameGTM: any, eventActionGTM: any, eventLabelGTM: any, featureGTM: any, pagenameGTM: any, userLoginStatus: any): void {
    const marketingFlag = this.userInputService.getValue('marketing_acceptance_flag')
    const marketingConsent = marketingFlag?.option_value === true ? 'yes' : 'no'

    const payload: any = {
      event: 'Track_KPI',
      eventname: eventnameGTM,
      event_action: eventActionGTM,
      event_label: eventLabelGTM,
      feature: featureGTM,
      pagename: pagenameGTM,
      system_type: 'wir_counsellor',
      office_id: this.getOfficeId(),
      user_login_status: userLoginStatus,
      destination_country: this.getStringValue(this.userInputService.getValue('preferredCountryCode')),
      intake_year: this.getStringValue(this.userInputService.getValue('studyPlanTimeline')),
      office_location: this.getOfficeLocation(),
      study_level: this.getStringValue(this.userInputService.getValue('preferredStudyLevel')),
      marketing_consent: marketingConsent,
      counselling_mode: this.getCounsellingMode()
    }
    const userId = this.getUserIdHash()
    if (userId) {
      payload.user_id = userId
    }
    this.getDataLayer().push(payload)
  }

  trackOptional (pagenameGTM: any, userLoginStatusGTM: any, featureGTM: any): void {
    const payload: any = {
      event: 'track_optional',
      eventname: 'wir_optional_fields',
      event_label: 'submit_button',
      event_action: 'submit',
      preferred_service: this.getOptionalFieldStatus(this.userInputService.getValue('preferredServiceGTM')),
      feature: featureGTM,
      pagename: pagenameGTM,
      system_type: 'wir_counsellor',
      wir_dob: this.getOptionalFieldStatus(this.userInputService.getValue('date_of_birth')),
      wir_gender: this.getOptionalFieldStatus(this.userInputService.getValue('gender')),
      wir_marital_status: this.getOptionalFieldStatus(this.userInputService.getValue('maritalStatus')),
      wir_exam_board_name: this.getOptionalFieldStatus(this.userInputService.getValue('examBoardName')),
      wir_study_mode: this.getOptionalFieldStatus(this.userInputService.getValue('studyMode')),
      wir_commencement_date: this.getOptionalFieldStatus(this.userInputService.getValue('studyStartDate')),
      wir_graduation_date: this.getOptionalFieldStatus(this.userInputService.getValue('studyEndDate')),
      wir_course_duration: this.getOptionalFieldStatus(this.userInputService.getValue('studyDuration')),
      wir_language_of_study: this.getOptionalFieldStatus(this.userInputService.getValue('studyLanguageName')),
      wir_grade_type: this.getOptionalFieldStatus(this.userInputService.getValue('gradeType')),
      wir_grade: this.getOptionalFieldStatus(this.userInputService.getValue('finalGrade')),
      wir_admission_test_status: (() => { const t = this.userInputService.getValue('academic_testType'); const l = t?.option_label || t?.option_value || t; return (l && String(l).trim() !== '') ? 'yes' : 'no'; })(),
      wir_test_name: this.getOptionalFieldStatus(this.userInputService.getValue('academic_testType')),
      wir_test_taken_date: this.getOptionalFieldStatus(this.userInputService.getValue('academic_testTakenDate')),
      wir_overall_test_score: this.getOptionalFieldStatus(this.userInputService.getValue('academic_overallScore')),
      office_location: this.getOfficeLocation(),
      office_id: this.getOfficeId(),
      user_login_status: userLoginStatusGTM
    }
    const userId = this.getUserIdHash()
    if (userId) {
      payload.user_id = userId
    }
    this.getDataLayer().push(payload)
  }

  private getOptionalFieldStatus (value: any): string {
    return this.hasFieldValue(value) ? 'yes' : 'no'
  }

  private getPresenceValue (value: any): string {
    return this.hasFieldValue(value) ? 'yes' : 'na'
  }

  private hasFieldValue (value: any): boolean {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim() !== ''
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') {
      const str = value.option_label || value.option_value || ''
      return str !== ''
    }
    return false
  }

  trackEvents (eventnameGTM: any, eventLabelGTM: any, eventActionGTM: any, pagenameGTM: any, userLoginStatusGTM: any, featureGTM: any): void {
    const payload: any = {
      event: 'track_events',
      eventname: eventnameGTM,
      event_label: eventLabelGTM,
      event_action: eventActionGTM,
      course_id: 'na',
      course_title: 'na',
      destination_country: this.getStringValue(this.userInputService.getValue('preferredCountryCode')),
      feature: featureGTM,
      preferred_service: this.getStringValue(this.userInputService.getValue('preferredServiceGTM')),
      institution_id: 'na',
      institution_name: 'na',
      keyword: 'na',
      pagename: pagenameGTM,
      system_type: 'wir_counsellor',
      parent_category: 'na',
      qualification_type: this.getStringValue(this.userInputService.getValue('qualificationType')),
      referral_page: 'na',
      study_level: this.getStringValue(this.userInputService.getValue('preferredStudyLevel')),
      intake_year: this.getStringValue(this.userInputService.getValue('studyPlanTimeline')),
      office_location: this.getOfficeLocation(),
      office_id: this.getOfficeId(),
      user_login_status: userLoginStatusGTM,
      study_mode: this.getStringValue(this.userInputService.getValue('studyMode')),
      recent_study_country: this.getStringValue(this.userInputService.getValue('studyCountryName')),
      counselling_mode: this.getCounsellingMode()
    }

    const userId = this.getUserIdHash()
    if (userId) {
      payload.user_id = userId
    }
    this.getDataLayer().push(payload)
  }
}
